import { useState } from "react"
import type { WidgetProps } from "lib/widget-types"
import { Pencil, ChevronRight } from "lucide-react"
import { useUser } from "contexts/UserContext"

interface NotesWidgetProps extends WidgetProps {
  onOpenNote?: () => void
}

function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return "Adesso"
  if (diffMinutes < 60) return `${diffMinutes} min fa`
  if (diffHours < 24) return `${diffHours} ore fa`
  if (diffDays === 1) return "Ieri"
  return `${diffDays} giorni fa`
}

export function NotesWidget({ widget, onOpenNote }: NotesWidgetProps) {
  const { user, loading } = useUser()
  const [lastModified] = useState<Date>(new Date()) // TODO: get from backend when available

  const notes = (user as any)?.notes || null

  const handleClick = () => {
    if (onOpenNote) {
      onOpenNote()
    }
  }

  // Check if notes is empty (including HTML that renders as empty like <p><br></p>)
  const isNotesEmpty = !notes || notes.replace(/<[^>]*>/g, '').trim() === ''
  const hasNotes = !isNotesEmpty

  return (
    <div
      className="rounded-xl backdrop-blur-sm p-4 border hexa-card-shadow h-full flex flex-col overflow-hidden"
      style={{
        backgroundColor: "var(--hexa-card-bg)",
        borderColor: "var(--hexa-border)",
      }}
    >
      {/* Header con icona Pencil a sinistra e freccia a destra */}
      <div onClick={handleClick} className="cursor-pointer flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Pencil style={{ width: 13, height: 13, color: "var(--hexa-purple)" }} />
            <h2 className="text-sm md:text-base font-semibold truncate" style={{ color: "var(--hexa-text-primary)" }}>
              {widget.title || "Note"}
            </h2>
          </div>
          <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "var(--hexa-purple)" }} />
        </div>
      </div>

      <div
        className="flex-1 min-h-0 flex flex-col cursor-pointer"
        onClick={handleClick}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm" style={{ color: "var(--hexa-text-secondary)" }}>
              Caricamento...
            </span>
          </div>
        ) : !hasNotes ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center h-full py-4">
            <div
              className="flex items-center justify-center mb-2.5"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                backgroundColor: "var(--hexa-empty-icon-bg, rgba(123, 63, 255, 0.1))",
              }}
            >
              <Pencil
                style={{
                  width: 17,
                  height: 17,
                  color: "var(--hexa-empty-icon-color, #7b3fff)",
                  opacity: 0.7,
                }}
              />
            </div>
            <span
              className="text-center mb-2"
              style={{ fontSize: 11, color: "var(--hexa-empty-text-color, rgba(255,255,255,0.4))" }}
            >
              Nessuna nota
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleClick()
              }}
              className="px-3 py-1.5 rounded-lg text-white transition-colors"
              style={{
                backgroundColor: "var(--hexa-purple)",
                fontSize: 11,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "var(--hexa-purple-hover)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "var(--hexa-purple)"
              }}
            >
              Scrivi una nota
            </button>
          </div>
        ) : (
          /* Notes Preview */
          <div className="flex flex-col h-full">
            <div
              className="flex-1 overflow-hidden px-1"
              style={{ maxHeight: 82 }}
            >
              <div
                className="text-sm leading-relaxed"
                style={{ color: "var(--hexa-text-primary)" }}
                dangerouslySetInnerHTML={{ __html: notes }}
              />
            </div>

            {/* Footer */}
            <div
              className="pt-2 mt-auto border-t"
              style={{ borderColor: "var(--hexa-border)" }}
            >
              <span style={{ fontSize: 10, color: "var(--hexa-text-muted)" }}>
                Modificato {formatTimeAgo(lastModified)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
